import { FC, useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Input,
  Space,
  Tabs,
  DatePicker,
  Spin,
  Alert,
  Modal,
  Descriptions,
  message,
  Popconfirm,
  Checkbox,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import {
  useInvoices,
  useDeleteInvoice,
  useBulkSendInvoices,
  getInvoicesForExport,
} from "~/services/billing/billing.queries";
import { Invoice } from "~/components/admin/billing/types";
import { getInvoicePdf } from "~/services/billing/billing.api";
import {
  getQuickBooksConnectionStatus,
  getQuickBooksAuthUrlWithPassword,
} from "~/services/quickbooks/quickbooks.api";
import { useAuth } from "~/store/auth.store";

const { RangePicker } = DatePicker;

export const ScreenInvoicing: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(
    null
  );
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [qbConnectionStatus, setQbConnectionStatus] = useState<any>(null);
  const [qbLoading, setQbLoading] = useState(true);
  const [connectingToQB, setConnectingToQB] = useState(false);

  // CSV Export state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Check QuickBooks connection status
  useEffect(() => {
    const checkQBStatus = async () => {
      try {
        setQbLoading(true);
        const status = await getQuickBooksConnectionStatus();
        setQbConnectionStatus(status);
      } catch (error) {
        console.error("Failed to check QuickBooks status:", error);
        setQbConnectionStatus({ isConnected: false });
      } finally {
        setQbLoading(false);
      }
    };
    checkQBStatus();
  }, []);

  // Handle QuickBooks auth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qbAuth = urlParams.get("qb_auth");
    const company = urlParams.get("company");
    const errorMessage = urlParams.get("message");

    if (qbAuth === "success") {
      message.success({
        content: `🎉 Successfully connected to QuickBooks! ${
          company ? `(${decodeURIComponent(company)})` : ""
        }`,
        duration: 5,
      });
      // Clean URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh connection status
      getQuickBooksConnectionStatus().then(setQbConnectionStatus).catch(console.error);
    } else if (qbAuth === "error") {
      message.error({
        content: `❌ QuickBooks connection failed: ${
          errorMessage ? decodeURIComponent(errorMessage) : "Unknown error"
        }`,
        duration: 8,
      });
      // Clean URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Handle QuickBooks connection
  const handleConnectQuickBooks = async () => {
    try {
      setConnectingToQB(true);
      // Prompt for password verification
      Modal.confirm({
        title: "Connect to QuickBooks",
        content: (
          <div>
            <p>You will be redirected to QuickBooks to authorize the connection.</p>
            <p className="text-sm text-gray-500 mt-2">
              Make sure your QuickBooks credentials are configured in the backend.
            </p>
          </div>
        ),
        onOk: async () => {
          try {
            const response = await getQuickBooksAuthUrlWithPassword("");
            if (response.authUrl) {
              window.location.href = response.authUrl;
            }
          } catch (error: any) {
            message.error(error?.response?.data?.message || "Failed to get QuickBooks auth URL");
            setConnectingToQB(false);
          }
        },
        onCancel: () => {
          setConnectingToQB(false);
        },
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to connect to QuickBooks");
      setConnectingToQB(false);
    }
  };

  // Map frontend tabs to API status values
  const getApiStatus = (tab: string) => {
    switch (tab) {
      case "pending":
        return "draft";
      case "invoiced":
        return "sent";
      case "paid":
        return "paid";
      case "overdue":
        return "overdue";
      default:
        return undefined;
    }
  };

  // Use real API call with correct status mapping
  const {
    data: invoicesData,
    isLoading,
    error,
    refetch,
  } = useInvoices({
    status: getApiStatus(activeTab),
    page: 1,
    limit: 50,
  });

  // Handle refresh from create invoice page using sessionStorage
  useEffect(() => {
    // Check if we're coming from create invoice page
    const isFromCreateInvoice = sessionStorage.getItem("fromCreateInvoice");
    if (isFromCreateInvoice === "true") {
      refetch();
      message.success({
        content: "Invoice list refreshed! 📋",
        duration: 3,
      });
      sessionStorage.removeItem("fromCreateInvoice");
    }
  }, [refetch]);

  const deleteInvoiceMutation = useDeleteInvoice();
  const bulkSendMutation = useBulkSendInvoices();

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedInvoiceIds([]);
  }, [activeTab]);

  // Generate CSV export filename
  const generateExportFilename = () => {
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS
    const userName = user?.firstName
      ? `${user.firstName}${user.lastName ? "_" + user.lastName : ""}`
      : "User";
    return `QB_Invoice_Export_${dateStr}_${timeStr}_${userName}.csv`;
  };

  // Helper to format service type for display
  const formatServiceType = (serviceType: string) => {
    const typeMap: Record<string, string> = {
      COLLECTION: "Collection",
      DELIVERY: "Delivery",
      ON_SITE_SERVICE: "On-Site Service",
      WAREHOUSE_SERVICE: "Warehouse Service",
      FABRICATION_SERVICE: "Fabrication",
    };
    return typeMap[serviceType] || serviceType || "Services";
  };

  // QB memo character limit (conservative)
  const QB_MEMO_LIMIT = 1000;

  // Generate abridged memo from service tickets
  const generateMemo = (invoice: any, serviceTickets: any[]) => {
    if (!serviceTickets || serviceTickets.length === 0) {
      return invoice.notes || "";
    }

    const jobTicket = serviceTickets[0]?.jobTicket;
    const refNumber = jobTicket?.referenceNumber || "";
    const sidemark = jobTicket?.sidemark || "";
    const projectName = jobTicket?.project?.name || "";
    const lines: string[] = [];

    // Add sidemark and project at the top (if applicable)
    const headerParts = [];
    if (sidemark) headerParts.push(`Sidemark: ${sidemark}`);
    if (projectName) headerParts.push(`Project: ${projectName}`);
    if (headerParts.length > 0) {
      lines.push(headerParts.join(" | "));
    }

    // Add reference
    if (refNumber) {
      lines.push(`Ref #${refNumber}`);
    }

    // Add each service as a line
    serviceTickets.forEach((ticket: any, index: number) => {
      const serviceType = formatServiceType(ticket.serviceType);
      const locationName =
        ticket.location?.addressName ||
        ticket.location?.name ||
        ticket.tempLocationData?.name ||
        ticket.tempLocationData?.city ||
        "";
      const scope = ticket.scope || "";
      const suffix = serviceTickets.length > 1 ? `-${String(index + 1).padStart(2, "0")}` : "";

      // Format: "1) Collection @ Gallery XYZ - Pack artwork"
      const parts = [`${index + 1}) ${serviceType}`];
      if (locationName) parts.push(`@ ${locationName}`);
      if (scope) parts.push(`- ${scope}`);
      if (suffix) parts.push(suffix);

      lines.push(parts.join(" "));
    });

    // Join with semicolons for compact format
    let memo = lines.join("; ");

    // If memo is too long, truncate and add ellipsis
    if (memo.length > QB_MEMO_LIMIT) {
      memo = memo.substring(0, QB_MEMO_LIMIT - 3) + "...";
    }

    return memo;
  };

  // Helper to extract invoice number without INV prefix
  const getInvoiceNumberDigits = (invoiceNumber: string) => {
    if (!invoiceNumber) return "";
    // Remove "INV" prefix and any leading zeros, keep the rest (including suffixes like -01)
    return invoiceNumber.replace(/^INV/i, "");
  };

  // Generate CSV content for QuickBooks import
  const generateCSVContent = (invoices: any[]) => {
    // QuickBooks CSV format headers
    const headers = [
      "InvoiceNo",
      "Customer",
      "InvoiceDate",
      "DueDate",
      "Terms",
      "Item",
      "ItemDescription",
      "ServiceDate",
      "ItemQuantity",
      "ItemRate",
      "ItemAmount",
      "CustomerNote",
      "AllowOnlineACHPayment",
      "Subtotal",
      "Tax",
      "Total",
    ];

    const rows: string[][] = [];

    invoices.forEach((invoice) => {
      const serviceTickets = invoice.serviceTickets || [];
      const lineItems = invoice.lineItems || [];
      const invoiceDate = invoice.invoiceDate
        ? new Date(invoice.invoiceDate).toLocaleDateString("en-US")
        : "";
      // Use actual due date from invoice, fallback to invoice date if not set
      const dueDate = invoice.dueDate
        ? new Date(invoice.dueDate).toLocaleDateString("en-US")
        : invoiceDate;
      const clientName = invoice.client?.companyName || "Unknown Client";
      // Extract just digits from invoice number (remove INV prefix)
      const invoiceNo = getInvoiceNumberDigits(invoice.invoiceNumber);

      // Generate memo from service tickets
      const memo = generateMemo(invoice, serviceTickets);

      // Get additional charges (line items without serviceTicketId)
      const additionalCharges = lineItems.filter(
        (item: any) => !item.serviceTicketId
      );

      // Track if this is the first row for invoice-level fields
      let isFirstRow = true;

      // If we have service tickets, use them for richer data
      if (serviceTickets.length > 0) {
        serviceTickets.forEach((ticket: any, index: number) => {
          // Get location name from the location relation or tempLocationData
          const locationName =
            ticket.location?.addressName ||
            ticket.location?.name ||
            ticket.tempLocationData?.name ||
            "";

          // Get address and state from shipping address
          const addressParts = [];
          const address = ticket.location?.address || ticket.tempLocationData?.address || "";
          const city = ticket.location?.city || ticket.tempLocationData?.city || "";
          const state = ticket.location?.state || ticket.tempLocationData?.state || "";
          if (address) addressParts.push(address);
          if (city) addressParts.push(city);
          if (state) addressParts.push(state);
          const fullAddress = addressParts.join(", ");

          // Get job ticket reference number
          const refNumber = ticket.jobTicket?.referenceNumber || "";

          // Build suffix for multiple services (-01, -02, etc.)
          const serviceSuffix = serviceTickets.length > 1
            ? `-${String(index + 1).padStart(2, "0")}`
            : "";

          // Build description: address/city/state + scope + location name + ref # with suffix
          const descParts = [];
          if (fullAddress) descParts.push(fullAddress);
          if (ticket.scope) descParts.push(ticket.scope);
          if (locationName && locationName !== city) descParts.push(locationName);
          if (refNumber) descParts.push(`Ref #${refNumber}${serviceSuffix}`);
          const itemDescription =
            descParts.length > 0 ? descParts.join(" - ") : "Art Handling Services";

          // Item is just the service type
          const item = formatServiceType(ticket.serviceType);

          // Get service date from ticket
          const serviceDate = ticket.serviceDatetime || ticket.startDatetime
            ? new Date(ticket.serviceDatetime || ticket.startDatetime).toLocaleDateString("en-US")
            : "";

          // Find matching line item for pricing, or use ticket data
          const matchingLineItem = lineItems[index] || {};
          const quantity = matchingLineItem.quantity || 1;
          const rate = matchingLineItem.unitPrice || matchingLineItem.rate || 0;
          const amount = quantity * rate;

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            item,
            itemDescription,
            serviceDate,
            String(quantity),
            String(rate),
            String(amount),
            isFirstRow ? memo : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });

        // Add additional charges (line items without serviceTicketId)
        additionalCharges.forEach((charge: any) => {
          const chargeDate = charge.date
            ? new Date(charge.date).toLocaleDateString("en-US")
            : "";

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            "", // Terms (already set on first row)
            charge.type === "supply" ? "Supply" : charge.type === "admin_charge" ? "Admin Charge" : "Additional Charge",
            charge.description || (charge.type === "admin_charge" ? "Admin Charge" : "Additional Charge"),
            chargeDate,
            String(charge.quantity || 1),
            String(charge.rate || charge.unitPrice || 0),
            String(charge.amount || (charge.quantity || 1) * (charge.rate || charge.unitPrice || 0)),
            "", // CustomerNote (already set on first row)
            "", // AllowOnlineACHPayment (already set on first row)
            "", // Subtotal (already set on first row)
            "", // Tax (already set on first row)
            "", // Total (already set on first row)
          ]);
        });
      } else if (lineItems.length > 0) {
        // Fall back to line items if no service tickets
        // Separate service items from additional charges
        const serviceItems = lineItems.filter((item: any) => item.serviceTicketId);
        const additionalItems = lineItems.filter((item: any) => !item.serviceTicketId);

        // Process service-related line items
        serviceItems.forEach((item: any) => {
          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            "Services",
            item.description || "Art Handling Services",
            "", // No service date available
            String(item.quantity || 1),
            String(item.unitPrice || item.rate || 0),
            String(
              item.amount ||
                (item.quantity || 1) * (item.unitPrice || item.rate || 0)
            ),
            isFirstRow ? invoice.notes || "" : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });

        // Process additional charges
        additionalItems.forEach((item: any) => {
          const chargeDate = item.date
            ? new Date(item.date).toLocaleDateString("en-US")
            : "";

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            item.type === "supply" ? "Supply" : item.type === "admin_charge" ? "Admin Charge" : "Additional Charge",
            item.description || (item.type === "admin_charge" ? "Admin Charge" : "Additional Charge"),
            chargeDate,
            String(item.quantity || 1),
            String(item.unitPrice || item.rate || 0),
            String(
              item.amount ||
                (item.quantity || 1) * (item.unitPrice || item.rate || 0)
            ),
            isFirstRow ? invoice.notes || "" : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });
      } else {
        // Invoice with no line items or service tickets - create a single row
        rows.push([
          invoiceNo,
          clientName,
          invoiceDate,
          dueDate,
          isFirstRow ? "Due on receipt" : "", // Terms
          "Services",
          invoice.description || "Art Handling Services",
          "", // No service date available
          "1",
          String(invoice.subtotal || invoice.total || 0),
          String(invoice.subtotal || invoice.total || 0),
          isFirstRow ? invoice.notes || "" : "",
          isFirstRow ? "true" : "", // AllowOnlineACHPayment
          isFirstRow ? String(invoice.subtotal || 0) : "",
          isFirstRow ? String(invoice.tax || 0) : "",
          isFirstRow ? String(invoice.total || 0) : "",
        ]);
      }
    });

    // Escape CSV values
    const escapeCSV = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    return csvContent;
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    if (selectedInvoiceIds.length === 0) {
      message.warning("Please select at least one invoice to export");
      return;
    }

    try {
      setIsExporting(true);

      // Get full invoice data for export
      const invoicesData = await getInvoicesForExport(selectedInvoiceIds);

      // Generate filename
      const filename = generateExportFilename();

      // Generate CSV content
      const csvContent = generateCSVContent(invoicesData);

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Only mark draft invoices as sent
      const draftInvoiceIds = invoicesData
        .filter((inv: any) => inv.status === "draft" || inv.status === "pending")
        .map((inv: any) => inv.id);

      if (draftInvoiceIds.length > 0) {
        const result = await bulkSendMutation.mutateAsync({
          ids: draftInvoiceIds,
          exportFilename: filename,
        });

        if (result.failed && result.failed.length > 0) {
          message.warning(
            `Exported ${selectedInvoiceIds.length} invoices. ${result.success.length} draft(s) marked as sent, ${result.failed.length} failed.`
          );
        } else {
          message.success(
            `Exported ${selectedInvoiceIds.length} invoice(s). ${draftInvoiceIds.length} draft(s) marked as sent.`
          );
        }
      } else {
        message.success(
          `Successfully exported ${selectedInvoiceIds.length} invoice(s)`
        );
      }

      // Clear selection and refresh
      setSelectedInvoiceIds([]);
      refetch();
    } catch (error: any) {
      console.error("Export error:", error);
      message.error(
        `Failed to export invoices: ${error?.message || "Unknown error"}`
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Handle select all for invoices (works on any tab)
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const invoiceIds = filteredInvoices.map((inv) => inv.id);
      setSelectedInvoiceIds(invoiceIds);
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  // Handle individual invoice selection
  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    if (checked) {
      setSelectedInvoiceIds((prev) => [...prev, invoiceId]);
    } else {
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    try {
      setDeletingInvoiceId(invoiceId);
      await deleteInvoiceMutation.mutateAsync(invoiceId);
      message.success({
        content: "Invoice deleted successfully! 🗑️",
        duration: 3,
      });
      refetch();
    } catch (error: any) {
      message.error({
        content: `Failed to delete invoice: ${
          error?.message || "Unknown error"
        }`,
        duration: 5,
      });
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const invoices = invoicesData?.invoices || [];

  // Filter invoices based on search text and status
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      (invoice.invoiceNumber || invoice.id)
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      invoice.client.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus =
      activeTab === "all" ||
      (activeTab === "pending" &&
        (invoice.status === "draft" || invoice.status === "pending")) ||
      (activeTab === "invoiced" && invoice.status === "sent") ||
      (activeTab === "paid" && invoice.status === "paid") ||
      (activeTab === "overdue" && invoice.status === "overdue");

    return matchesSearch && matchesStatus;
  });

  // Check if we're on the draft tab
  const isDraftTab = activeTab === "pending";

  // Get all invoices for select all calculation (now works on any tab)
  const allSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((inv) => selectedInvoiceIds.includes(inv.id));
  const someSelected =
    filteredInvoices.some((inv) => selectedInvoiceIds.includes(inv.id)) &&
    !allSelected;

  const columns = [
    // Checkbox column - show on all tabs for CSV export
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
      width: 50,
      render: (_: any, record: Invoice) => (
        <Checkbox
          checked={selectedInvoiceIds.includes(record.id)}
          onChange={(e) =>
            handleSelectInvoice(record.id, e.target.checked)
          }
        />
      ),
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (invoiceNumber: string, record: Invoice) =>
        invoiceNumber || record.id,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => `$${amount?.toFixed(2) || "0.00"}`,
      sorter: (a: Invoice, b: Invoice) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Invoice["status"]) => {
        const statusConfig = {
          draft: { color: "blue", label: "Draft" },
          pending: { color: "orange", label: "Pending" },
          sent: { color: "purple", label: "Sent" },
          paid: { color: "green", label: "Paid" },
          overdue: { color: "red", label: "Overdue" },
          cancelled: { color: "gray", label: "Cancelled" },
        };
        return (
          <Tag color={statusConfig[status]?.color || "default"}>
            {statusConfig[status]?.label || status}
          </Tag>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Invoice) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedInvoice(record);
              setViewModalVisible(true);
            }}
          >
            View
          </Button>

          <Popconfirm
            title="Are you sure you want to delete this invoice?"
            description="This action cannot be undone."
            onConfirm={() => {
              handleDeleteInvoice(record.id);
            }}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingInvoiceId === record.id}
              danger
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "all",
      label: "All Invoices",
      children: null,
    },
    {
      key: "pending",
      label: "Draft",
      children: null,
    },
    {
      key: "invoiced",
      label: "Sent",
      children: null,
    },
    {
      key: "paid",
      label: "Paid",
      children: null,
    },
    {
      key: "overdue",
      label: "Overdue",
      children: null,
    },
  ];

  // Function to handle viewing the PDF
  const handleViewPdf = async () => {
    if (!selectedInvoice) return;

    try {
      setIsPdfLoading(true);
      const pdfBlob = await getInvoicePdf(selectedInvoice.id);

      // Create a URL for the blob with proper MIME type
      const pdfUrl = URL.createObjectURL(
        new Blob([pdfBlob], { type: "application/pdf" })
      );

      // Try to open in a new tab
      const newWindow = window.open(pdfUrl, "_blank");

      // If opening in a new tab fails, offer a download option
      if (
        !newWindow ||
        newWindow.closed ||
        typeof newWindow.closed === "undefined"
      ) {
        // Create a download link
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `invoice-${
          selectedInvoice.invoiceNumber || selectedInvoice.id
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.info(
          "Invoice PDF downloaded. Some browsers may block opening PDFs directly."
        );
      } else {
        message.success("Invoice PDF opened successfully");
      }

      // Clean up the URL object after a delay
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 30000);
    } catch (error: any) {
      console.error("PDF error:", error);

      // Extract error details from the response
      let errorMessage = error?.message || "";
      let errorDetail = "";

      // Check if the error is from the backend with additional details
      if (error?.response?.data) {
        const responseData = error.response.data;
        errorMessage = responseData.message || errorMessage;
        errorDetail = responseData.detail || "";
      }

      // Handle specific error cases
      if (
        errorMessage.includes("not found in QuickBooks") ||
        errorMessage.includes("Object Not Found")
      ) {
        Modal.error({
          title: "Invoice Not Found in QuickBooks",
          content: (
            <div>
              <p>
                {errorDetail ||
                  "This invoice could not be found in QuickBooks. This could be because:"}
              </p>
              <ul className="list-disc pl-5 mt-2">
                <li>
                  The invoice has been deleted or made inactive in QuickBooks
                </li>
                <li>The invoice was never properly synced with QuickBooks</li>
                <li>There's a connection issue with QuickBooks</li>
              </ul>
              <p className="mt-3">
                Please check the invoice status in QuickBooks or contact your
                administrator.
              </p>
            </div>
          ),
        });
      } else if (errorMessage.includes("QuickBooks")) {
        message.error(
          `QuickBooks error: ${
            errorDetail ||
            "Unable to retrieve the invoice PDF. Please check your QuickBooks connection."
          }`
        );
      } else {
        message.error(
          `Failed to load invoice PDF: ${errorMessage || "Unknown error"}`
        );
      }
    } finally {
      setIsPdfLoading(false);
    }
  };

  if (error) {
    return (
      <div>
        <PageMeta
          title="Invoicing | Art Handler"
          description="Manage invoices for art handling services"
        />
        <BillingSubNav activeKey="invoicing" />
        <Alert
          message="Error Loading Invoices"
          description="Failed to load invoice data. Please check your connection to the backend."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <PageMeta
        title="Invoicing | Art Handler"
        description="Manage invoices for art handling services"
      />

      <BillingSubNav activeKey="invoicing" />

      {/* QuickBooks Connection Status */}
      {!qbLoading && (
        <Card className="mb-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {qbConnectionStatus?.isConnected ? (
                <>
                  <CheckCircleOutlined className="text-green-500 text-2xl" />
                  <div>
                    <div className="font-semibold text-green-700">
                      QuickBooks Connected
                    </div>
                    {qbConnectionStatus?.companyName && (
                      <div className="text-sm text-gray-600">
                        Company: {qbConnectionStatus.companyName}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <CloseCircleOutlined className="text-red-500 text-2xl" />
                  <div>
                    <div className="font-semibold text-red-700">
                      QuickBooks Not Connected
                    </div>
                    <div className="text-sm text-gray-600">
                      Connect to sync invoices with QuickBooks
                    </div>
                  </div>
                </>
              )}
            </div>
            <Button
              type={qbConnectionStatus?.isConnected ? "default" : "primary"}
              icon={<LinkOutlined />}
              onClick={handleConnectQuickBooks}
              loading={connectingToQB}
            >
              {qbConnectionStatus?.isConnected ? "Reconnect" : "Connect to QuickBooks"}
            </Button>
          </div>
          {qbConnectionStatus?.error && (
            <Alert
              message="Connection Error"
              description={qbConnectionStatus.error}
              type="error"
              className="mt-4"
              showIcon
            />
          )}
        </Card>
      )}

      {/* Controls */}
      <Card className="mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search invoices..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="max-w-md"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <RangePicker className="w-full sm:w-auto" />
            {selectedInvoiceIds.length > 0 && (
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
                loading={isExporting}
                className="bg-gradient-to-r from-green-500 to-green-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Export CSV ({selectedInvoiceIds.length})
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate("/billing/invoicing/create")}
              className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Create New Invoice
            </Button>
          </div>
        </div>
      </Card>

      {/* Invoice List */}
      <Card className="shadow-sm">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="mb-4"
        />

        {selectedInvoiceIds.length > 0 && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <span>
                  <strong>{selectedInvoiceIds.length} invoice(s) selected</strong>
                  {" "}- Ready to export as CSV for QuickBooks import.
                  {isDraftTab && (
                    <span className="text-gray-500 ml-1">
                      (Draft invoices will be marked as Sent after export)
                    </span>
                  )}
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setSelectedInvoiceIds([])}
                >
                  Clear Selection
                </Button>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />
        )}

        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={filteredInvoices}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: isLoading
                ? "Loading..."
                : "No invoices found. Connect to backend to load real data.",
            }}
          />
        </Spin>
      </Card>

      {/* View Modal */}
      {selectedInvoice && (
        <Modal
          visible={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          footer={[
            // <Button
            //   key="back"
            //   onClick={() => {
            //     setViewModalVisible(false);
            //     navigate("/billing/invoicing");
            //   }}
            // >
            //   Back to Invoice List
            // </Button>,
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="print"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleViewPdf}
              loading={isPdfLoading}
            >
              View/Download PDF
            </Button>,
          ]}
          width={900}
          title={
            <div className="flex items-center space-x-3">
              <FileTextOutlined className="text-blue-600" />
              <span className="text-xl font-semibold">Invoice Details</span>
            </div>
          }
          className="invoice-detail-modal"
        >
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg">
            {/* Header Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {selectedInvoice.invoiceNumber || selectedInvoice.id}
                  </h2>
                  <div className="text-gray-600">
                    <div className="flex items-center space-x-2 mb-1">
                      <UserOutlined className="text-blue-500" />
                      <span className="font-medium">Client:</span>
                      <span>{selectedInvoice.client}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarOutlined className="text-green-500" />
                      <span className="font-medium">Date:</span>
                      <span>
                        {selectedInvoice.date
                          ? new Date(selectedInvoice.date).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    ${selectedInvoice.amount?.toFixed(2) || "0.00"}
                  </div>
                  <Tag
                    color={
                      selectedInvoice.status === "paid"
                        ? "green"
                        : selectedInvoice.status === "sent"
                        ? "blue"
                        : selectedInvoice.status === "overdue"
                        ? "red"
                        : "orange"
                    }
                    className="text-sm px-3 py-1"
                  >
                    {selectedInvoice.status?.toUpperCase() || "DRAFT"}
                  </Tag>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FileTextOutlined className="mr-2 text-blue-500" />
                Invoice Information
              </h3>
              <Descriptions
                bordered
                column={2}
                size="middle"
                className="custom-descriptions"
              >
                <Descriptions.Item label="Invoice Number" span={1}>
                  <span className="font-mono text-blue-600">
                    {selectedInvoice.invoiceNumber || selectedInvoice.id}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={1}>
                  <Tag
                    color={
                      selectedInvoice.status === "paid"
                        ? "green"
                        : selectedInvoice.status === "sent"
                        ? "blue"
                        : selectedInvoice.status === "overdue"
                        ? "red"
                        : "orange"
                    }
                  >
                    {selectedInvoice.status?.toUpperCase() || "DRAFT"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Client" span={1}>
                  <div className="flex items-center">
                    <UserOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.client}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Amount" span={1}>
                  <span className="text-lg font-semibold text-green-600">
                    ${selectedInvoice.amount?.toFixed(2) || "0.00"}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Date" span={1}>
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.date
                      ? new Date(selectedInvoice.date).toLocaleDateString()
                      : "N/A"}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Due Date" span={1}>
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.date
                      ? new Date(
                          new Date(selectedInvoice.date).getTime() +
                            30 * 24 * 60 * 60 * 1000
                        ).toLocaleDateString()
                      : "N/A"}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* QuickBooks Integration Info */}
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <DollarOutlined className="mr-2 text-green-500" />
                QuickBooks Integration
              </h3>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse mr-3"></div>
                  <span className="text-green-700 font-medium">
                    Synced with QuickBooks
                  </span>
                </div>
                <Button
                  type="link"
                  disabled={!selectedInvoice.quickbooksInvoiceId}
                  onClick={() => {
                    if (selectedInvoice.quickbooksInvoiceId) {
                      const url = `https://sandbox.qbo.intuit.com/app/invoice?txnId=${selectedInvoice.quickbooksInvoiceId}`;
                      window.open(url, "_blank");
                    }
                  }}
                >
                  View in QuickBooks
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ScreenInvoicing;
